import {
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraText,
  EquipUITheme,
} from '@equip.health/ui';
import { isObject } from 'formik';
import { DateTime } from 'luxon';

import { SurveyInstanceData } from '~/lib/types/assessments/SurveyInstanceData';
import {
  buildResolvedTitleLookup,
  checkIfQuestionIsVisible,
  getAllQuestions,
  getAnswerTitle,
} from '~/lib/util/survey.util';
import util from '~/lib/util/util';
import { removeUrlsFromText } from '~/lib/utils';

const { captionBold, paragraphBold, h6, label, paragraph } =
  EquipUITheme.typography;

type HeaderRowProps = { data: SurveyInstanceData[]; id: string };
type AssessmentsTableProps = {
  id: string;
  tableData: SurveyInstanceData[];
};

const HeaderRow = ({ data, id }: HeaderRowProps) => {
  return (
    <ChakraSimpleGrid
      alignItems="center"
      background="neutral.background.secondary"
      borderRadius="16px"
      gap="16px"
      id={`${id}-container`}
      marginBottom="8px"
      padding="8px 24px"
      templateColumns={`repeat(${3 + data.length}, 1fr)`}
    >
      <ChakraGridItem
        {...captionBold}
        alignItems="center"
        color="secondary.50"
        colSpan={3}
        fontSize="sm"
        id={`${id}-title`}
      >
        Question
      </ChakraGridItem>
      {data.map(({ completedBy, dateDisplayLabel, externalId }) => (
        <ChakraGridItem
          {...h6}
          alignItems="center"
          colSpan={1}
          id={`${id}-row-container-${externalId}`}
          key={`${id}-row-${externalId}`}
        >
          <ChakraFlex flexDirection="column" id={`${id}-row-${externalId}`}>
            <ChakraText fontWeight="bold" id={`${id}-date-${externalId}`}>
              {DateTime.fromFormat(dateDisplayLabel, 'LL/dd').toFormat(
                'dd LLL',
              )}
            </ChakraText>
            <ChakraText
              color="neutral.secondary"
              id={`${id}-submitted-by-label-${externalId}`}
            >
              Submitted by
            </ChakraText>
            <ChakraText
              color="secondary.50"
              id={`${id}-completed-by-label-${externalId}`}
            >
              {completedBy}
            </ChakraText>
          </ChakraFlex>
        </ChakraGridItem>
      ))}
    </ChakraSimpleGrid>
  );
};
const listItemStyles = {
  container: {
    background: 'neutral.background.primary',
    borderColor: 'neutral.line',
    borderRadius: '16px',
    borderWidth: '1px',
    marginBottom: '2px',
    padding: '8px 24px',
  },
  mainGrid: {
    alignItems: 'center',
    gap: '16px',
  },
};

const getTrimmedLowerCaseString = (input: string): string => {
  if (typeof input === 'string' || typeof input === 'number') {
    return input.toString().replaceAll(' ', '').toLowerCase();
  }
  return '';
};

/**  Table Body is constructed by parsing the surveyDefinition and jsonResults from
 the assessmentsData for one Survey. A object is created with key as the question key
 and value as array of objects where each Object corresponds to one survey instance.
 Each object has the question, answer, number of choices, external ID and the option selected. */
const getTableBody = (assessmentsData: SurveyInstanceData[], id: string) => {
  const obj = {};
  assessmentsData.forEach(
    ({ surveyDefinition, jsonResults, externalId }, assessmentIndex) => {
      const parsedData: SurveyDefinition = util.parseJson(surveyDefinition);
      const parsedJson = util.parseJson(jsonResults);
      const questions: SurveyQuestion[] = getAllQuestions(parsedData);
      const resolveTitle = buildResolvedTitleLookup(parsedData, parsedJson);

      for (const question of questions) {
        const {
          choices,
          columns,
          html,
          isRequired,
          name: questionKey,
          title,
          type,
          visible,
          visibleIf,
        } = question;
        const resolvedTitle = title && resolveTitle(questionKey);
        if (checkIfQuestionIsVisible(visibleIf, visible, parsedJson)) {
          const answer = parsedJson[questionKey];
          const answerComment =
            parsedJson[`${questionKey}-Comment`] ||
            parsedJson[`${questionKey}:-Comment`];
          const answerLine = getAnswerTitle(
            questions,
            questionKey,
            answer,
            answerComment,
          )
            .map((answerText: string) => {
              return removeUrlsFromText(answerText, '[Link]');
            })
            .join('\n\n');
          obj[questionKey] = obj[questionKey] || [];

          if (assessmentIndex !== obj[questionKey].length) {
            // If one assessment does not have a question then we are putting in empty obj for that assessment
            while (obj[questionKey].length < assessmentIndex) {
              obj[questionKey].push({
                answerLine: null,
                choices: null,
                columns,
                externalId: null,
                questionLine: resolvedTitle,
                type,
                value: null,
              });
            }
          }
          obj[questionKey].push({
            answerLine,
            choices,
            columns,
            externalId,
            html,
            isRequired,
            questionLine: resolvedTitle,
            type,
            value: answer,
          });
        }
      }
    },
  );

  return Object.keys(obj).map((eachQuestionKey: string, ind: number) => {
    const question = obj[eachQuestionKey];
    const { columns, html, isRequired, questionLine, type, value } =
      question[0];
    return (
      <ChakraBox
        {...listItemStyles.container}
        id={`${id}-container-${getTrimmedLowerCaseString(eachQuestionKey)}`}
        key={`${id}-container-${getTrimmedLowerCaseString(eachQuestionKey)}`}
      >
        <ChakraSimpleGrid
          {...listItemStyles.mainGrid}
          id={`${id}-main-grid-${getTrimmedLowerCaseString(eachQuestionKey)}`}
          templateColumns={`repeat(${3 + assessmentsData.length}, 1fr)`}
        >
          <ChakraGridItem
            colSpan={3}
            id={`${id}-question-container-${getTrimmedLowerCaseString(
              eachQuestionKey,
            )}`}
          >
            <ChakraFlex>
              <ChakraText
                {...paragraphBold}
                id={`${id}-question-index-${getTrimmedLowerCaseString(
                  eachQuestionKey,
                )}`}
              >
                {ind + 1}
              </ChakraText>
              <ChakraText
                {...paragraph}
                id={`${id}-question-label-${getTrimmedLowerCaseString(
                  eachQuestionKey,
                )}`}
                marginLeft="12px"
              >
                {html && (
                  <div
                    // Replacing newline characters which are represented as escaped characters and excaped quotation marks
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: html.replace(/\\n/g, '').replace(/\\"/g, '"'),
                    }}
                  />
                )}
                {!html && questionLine && (
                  <ChakraText>{`${questionLine.replace(/^\d+\.\s+/, '')} ${
                    isRequired ? ' *' : ''
                  }`}</ChakraText>
                )}
                {Array.isArray(value) && type === 'matrixdynamic'
                  ? value.map((eachSubQuestion: SurveyQuestion) =>
                      Object.keys(eachSubQuestion).map((subQuestionKey) => (
                        <ChakraText
                          {...label}
                          id={`${id}-sub-question-label-${getTrimmedLowerCaseString(
                            subQuestionKey,
                          )}`}
                          key={`${id}-sub-question-label-${getTrimmedLowerCaseString(
                            subQuestionKey,
                          )}`}
                        >
                          {columns.find(({ name }) => name === subQuestionKey)
                            ?.title ?? subQuestionKey}
                        </ChakraText>
                      )),
                    )
                  : null}
                {isObject(value) && type === 'matrixdropdown'
                  ? Object.keys(value).map((eachSubQuestion) => (
                      <ChakraText
                        {...label}
                        id={`${id}-sub-question-label-${getTrimmedLowerCaseString(
                          eachSubQuestion,
                        )}`}
                        key={`${id}-sub-question-label-${getTrimmedLowerCaseString(
                          eachSubQuestion,
                        )}`}
                      >
                        {eachSubQuestion}
                      </ChakraText>
                    ))
                  : null}
              </ChakraText>
            </ChakraFlex>
          </ChakraGridItem>
          {question.map(
            ({ answerLine, externalId, type, value }, ind: number) => (
              <ChakraGridItem
                alignContent="center"
                colSpan={1}
                id={`${id}-answer-container-${externalId}-${ind + 1}`}
                key={`${id}-answer-${externalId}-${ind + 1}`}
                maxHeight="110px"
                overflowY="auto"
              >
                <ChakraText
                  {...label}
                  backgroundColor="transparent"
                  borderRadius="8px"
                  id={`${id}-answer-${externalId}-${ind + 1}`}
                  padding="10px"
                  whiteSpace="pre-wrap"
                >
                  {Array.isArray(value) && type === 'matrixdynamic' ? (
                    <ChakraText marginTop="20px">
                      {value.map((eachSubAnswer) =>
                        Object.values(eachSubAnswer).map((answer, subInd) => (
                          <ChakraText
                            id={`${id}-sub-answer-${externalId}-${
                              ind + 1
                            }-${answer}-${subInd + 1}`}
                            key={`${id}-sub-answer-${externalId}-${
                              ind + 1
                            }-${answer}-${subInd + 1}`}
                          >
                            {answer.toString()}
                          </ChakraText>
                        )),
                      )}
                    </ChakraText>
                  ) : null}
                  {isObject(value) && type === 'matrixdropdown'
                    ? Object.keys(value).map((eachSubQuestion) =>
                        Object.values(value[eachSubQuestion]).map(
                          (subAnswer: string) => (
                            <ChakraText
                              {...label}
                              id={`${id}-sub-answer-label-${getTrimmedLowerCaseString(
                                subAnswer,
                              )}`}
                              key={`${id}-sub-answer-label-${getTrimmedLowerCaseString(
                                subAnswer,
                              )}`}
                            >
                              {subAnswer}
                            </ChakraText>
                          ),
                        ),
                      )
                    : null}
                  {type !== 'matrixdropdown' &&
                    type !== 'matrixdynamic' &&
                    answerLine}
                </ChakraText>
              </ChakraGridItem>
            ),
          )}
        </ChakraSimpleGrid>
      </ChakraBox>
    );
  });
};

const AssessmentsTable = ({ tableData, id }: AssessmentsTableProps) => {
  return (
    <ChakraBox id={`${id}__container`} width="100%">
      <HeaderRow data={tableData} id={`${id}__header`} />
      <ChakraBox maxHeight="70vh" overflowY="auto">
        {getTableBody(tableData, `${id}__table-body`)}
      </ChakraBox>
    </ChakraBox>
  );
};

export default AssessmentsTable;
